import type { RouteRecordName } from 'vue-router'

import { toRefs, useStorage, syncRef } from '@vueuse/core'
import { useRouteQuery } from '@vueuse/router'
import { useRoute } from 'vue-router'
import { watch, readonly } from 'vue'

export interface OrderingProps {
  /** Custom name for storing ordering preferences */
  orderingConfigName?: RouteRecordName
}

/**
 * Synchronizes ordering state between URL query parameters and localStorage,
 * with automatic persistence across page reloads.
 *
 * @param props - Configuration options
 * @returns Ordering state and update handlers
 */
export default <T extends string = string>(props: OrderingProps) => {
  const route = useRoute()

  interface Preferences {
    orderingDirection: '-' | '+'
    ordering: T
    paginateBy: number
  }

  const preferences = useStorage<Preferences>(`route-preferences:${props.orderingConfigName?.toString() ?? route.name?.toString() ?? '*'}`, () => ({
    orderingDirection: route.meta.orderingDirection ?? '-',
    ordering: route.meta.ordering ?? 'creation_date',
    paginateBy: route.meta.paginateBy ?? 50
  }))

  // TODO: I would like to replace the prefix `pref` with something that feels more solid.
  // The implicit renaming here somewhat obscures the author's intention...
  const {
    orderingDirection: prefOrderingDirection,
    paginateBy: prefPaginateBy,
    ordering: prefOrdering
  } = toRefs(preferences, {
    replaceRef: false
  })

  /**
   * Normalizes ordering direction for URL query parameters.
   * @param direction - '+' or some other string
   * @returns Empty string for ascending, '-' for descending
   */
  const normalizeDirection = (direction: string) =>
    direction === '+'
      ? ''
      : '-'

  /* TODO: Has to conform to `"-artist_credit__artist__modification_date" | "-creation_date" | "-random" | "-related" | "-release_date" | "-title" | "artist_credit__artist__modification_date" | "creation_date" | "random" | "related" | "release_date" | "title"` if Album; `"-creation_date" | "-id" | "-modification_date" | "-name" | "-random" | "-related" | "creation_date" | "id" | "modification_date" | "name" | "random" | "related"` if Artist; etc. See generated/types.ts */
  const queryOrdering = useRouteQuery<string | undefined>(
    'ordering',
    normalizeDirection(prefOrderingDirection.value) + prefOrdering.value,
    { transform: (value) => value?.trim() }
  )

  const queryPaginateBy = useRouteQuery('paginateBy', prefPaginateBy.value, {
    transform: Number
  })

  // NOTE: Sync paginateBy in query string and preferences. We're using `flush: 'post'` to make sure that we sync after all updates are done
  syncRef(queryPaginateBy, prefPaginateBy, {
    flush: 'post'
  })

  // NOTE: Sync ordering from preferences to query string
  watch([prefOrderingDirection, prefOrdering], () => {
    queryOrdering.value = normalizeDirection(prefOrderingDirection.value) + prefOrdering.value.trim()
  })

  // NOTE: Sync ordering from query string to preferences
  watch(queryOrdering, (ordering) => {
    prefOrderingDirection.value = ordering?.[0] === '-' ? '-' : '+'
    prefOrdering.value = ordering?.replace(/^[+-]/, '')
  }, { immediate: true })

/**
   * Registers a callback to execute when ordering preferences change.
   * @param fn - Callback function to execute on updates
   * @returns Watcher cleanup function
   * NOTE: We're using `flush: 'post'` to make sure that the `onOrderingUpdate` callback is called after all updates are done
   */
  const onOrderingUpdate = (fn: () => void) => watch(preferences, fn, {
    flush: 'post'
  })

  return {
    paginateBy: prefPaginateBy,
    ordering: prefOrdering,
    orderingDirection: prefOrderingDirection,
    orderingString: readonly(queryOrdering),
    onOrderingUpdate
  }
}
